import { Dialog, DialogTitle, DialogContent, Button } from '@mui/material';
import { React, useState, useMemo, useEffect } from 'react';
import { useMyContext } from '../../../context/MyContext';
import { RiMoneyRupeeCircleFill } from "react-icons/ri";
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from "react-icons/io";
import '../../../css/custom-scrollbar.css';
import { IoIosCloseCircle } from "react-icons/io";
import { CalculatorIcon, Check, Lock, Truck, Zap, PackagePlus, Rocket } from "lucide-react";

const slabs = [
  {
    id: 1,
    weight: "250 gm",
    label: "Light",
    gradient: "from-purple-500 to-indigo-500",
    maxGm: 250,
  },
  {
    id: 2,
    weight: "500 gm",
    label: "Standard",
    gradient: "from-teal-500 to-cyan-600",
    maxGm: 500,
  },
  {
    id: 3,
    weight: "1 Kg",
    label: "Bulk",
    gradient: "from-pink-500 to-red-600",
    maxGm: 1000,
  },
  {
    id: 4,
    weight: "5 kg",
    label: "Heavy",
    gradient: "from-green-400 to-emerald-600",
    maxGm: 5000,
  },
  {
    id: 5,
    weight: "10 kg",
    label: "Dense",
    gradient: "from-orange-400 to-yellow-500",
    maxGm: 10000,
  },
];

// Form mein jo weight (gm) submit hui thi, uske hisaab se matching slab id nikalta hai
const getSlabIdFromWeight = (weightInGrams) => {
  if (!weightInGrams && weightInGrams !== 0) return null;
  const match = slabs.find((slab) => weightInGrams <= slab.maxGm);
  return match ? match.id : slabs[slabs.length - 1].id;
};

// 4 pricing modes ki display config — icon, short label, full label
const MODE_CONFIG = {
  surface: { label: "Surface", icon: Truck },
  express: { label: "Express", icon: Zap },
  surface_per500: { label: "Surface +500g", icon: PackagePlus },
  express_per500: { label: "Express +500g", icon: Rocket },
};

// Courier ke naam se uska logo JSX return karta hai
const CourierLogo = ({ courier, isDarkMode }) => {
  const key = courier?.toLowerCase();

  if (key === "delhivery") {
    return (
      <img
        src={isDarkMode ? "/assets/images/DELHIVERYWhite.png" : "/assets/images/Delhivery.png"}
        alt="Delhivery"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "ecom") {
    return (
      <img
        src={isDarkMode ? "/assets/images/logoecom.svg" : "/assets/images/ecom-express-logo.png"}
        alt="Ecom Express"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "ekart") {
    return (
      <img
        src="/assets/images/ekart.png"
        alt="Ekart"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "xpressbees") {
    return (
      <img
        src="/assets/images/xpressbess.png"
        alt="XpressBees"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "bluedart") {
    return (
      <img
        src="/assets/images/bluedart.png"
        alt="Bluedart"
        className="h-6 w-14  object-contain"
      />
    );
  }
  if (key === "pickupxpress") {
    return (
      <img
        src="/assets/images/logo1.png"
        alt="Pickupxpress"
        className="h-6 w-14 object-contain"
      />
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
      {courier}
    </span>
  );
};

const ViewRateForword = ({ isopen, onClose, data }) => {
  const { isDarkMode } = useMyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const userData = JSON.parse(localStorage.getItem('user')) || {};

  // Submitted weight ke basis par kaunsa slab lock/select hona chahiye
  const lockedSlabId = useMemo(() => getSlabIdFromWeight(data?.requestWeight), [data]);

  const [active, setActive] = useState(lockedSlabId || 1);

  // Har courier card ka apna active pricing mode track karne ke liye
  // (surface / express / surface_per500 / express_per500)
  const [activeMode, setActiveMode] = useState({});

  // Jab bhi naya data (naya submit) aaye, active tab ko locked slab par sync kar do
  useEffect(() => {
    if (lockedSlabId) {
      setActive(lockedSlabId);
    }
  }, [lockedSlabId]);

  // Jab naya data aaye, sabhi courier cards ka default mode "surface" set kar do
  useEffect(() => {
    if (data?.data?.partners_pricing) {
      const defaults = {};
      Object.keys(data.data.partners_pricing).forEach((partner) => {
        defaults[partner] = 'surface';
      });
      setActiveMode(defaults);
    }
  }, [data]);

  const handleNavigation = () => {
    navigate('/shipments/forward');
  };

  const handleClose = () => {
    onClose();
  };

  // Courier-wise grouped pricing data — har courier ke andar 4 modes
  const partnersData = useMemo(() => {
    if (!data?.data?.partners_pricing) return [];

    return Object.keys(data.data.partners_pricing).map((partner) => ({
      courier: partner,
      zone: data.data.zone,
      modes: data.data.partners_pricing[partner] || {},
    }));
  }, [data]);

  // Kisi courier ka current selected mode return karta hai
  const getActiveMode = (courier) => activeMode[courier] || 'surface';

  // Courier card ke andar jab mode chip click ho to mode switch karta hai
  const setModeFor = (courier, mode) => {
    setActiveMode((prev) => ({ ...prev, [courier]: mode }));
  };

  return (
    <>
      <Dialog
        open={isopen}
        onClose={onClose}
        maxWidth="sm"
        className={`shadow-custom-light rounded-xl w-[100%] dark:shadow-custom-dark mt-1 ml-2 mr-2 p-1.5
    ${isDarkMode ? "dark:bg-darkmodeColor text-white" : ""}`}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            height: '96vh',
            backdropFilter: "blur(5px) sepia(5%)",
            borderRadius: "24px",
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle className="bg-customPurple text-white text-center">
          <h2 className="text-xl font-semibold flex items-center justify-center">
            <CalculatorIcon className="mr-2" size={20} />
            Rate Calculator
          </h2>
          <IoIosCloseCircle
            className="absolute top-2 right-2 cursor-pointer"
            size={32}
            onClick={handleClose}
          />
        </DialogTitle>


        <div className="w-full p-1 bg-white rounded-3xl">
          <h2 className="text-md font-medium mb-1 px-2 text-slate-800">
            Select Weight Slab
          </h2>

          <div className="grid grid-cols-2 px-2 md:grid-cols-5 gap-4">
            {slabs.map((item) => {
              const isActive = active === item.id;

              const isDisabled = lockedSlabId ? item.id !== lockedSlabId : false;

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (isDisabled) return;
                    setActive(item.id);
                  }}
                  className={`relative rounded-2xl p-1 transition-all duration-300 border
            ${isDisabled ? "cursor-not-allowed opacity-40 pointer-events-none" : "cursor-pointer"}
            ${isActive
                      ? `bg-gradient-to-br ${item.gradient} text-white shadow-xl scale-105`
                      : "bg-slate-200 border-gray-500 text-slate-700 hover:shadow-md"
                    }`}
                >

                  {/* {isActive && (
                    <div className="absolute top-3 right-3 bg-white text-black rounded-full p-1">
                      <Check size={12} />
                    </div>
                  )} */}

                  {isDisabled && (
                    <div className="absolute top-1 right-1 text-slate-500">
                      <Lock size={14} />
                    </div>
                  )}


                  <h3 className="text-md text-center font-semibold">{item.weight}</h3>


                  {/* <p className="text-sm opacity-80">{item.label}</p> */}
                </div>
              );
            })}
          </div>
        </div>
        <div className='mt-1 px-3 text-gray-800 text-[14px]'>Compare and choose the best courier partner</div>


        <DialogContent
          className={` ml-1 mr-1 custom-scrollbar flex-1
    ${isDarkMode ? "dark:bg-darkmodeColor text-white" : ""}`}
          sx={{
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          {partnersData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-1">
              {partnersData.map(({ courier, zone, modes }) => {
                const mode = getActiveMode(courier);
                const pricing = modes[mode];

                return (
                  <div
                    key={courier}
                    className="rounded-2xl border border-slate-800 dark:border-slate-700 bg-white dark:bg-darkmodeColor shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    {/* Header: logo + zone */}
                    <div className="flex items-center justify-between px-3 pt-1">
                      <CourierLogo courier={courier} isDarkMode={isDarkMode} />
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-100 border border-purple-700 text-customPurple dark:bg-purple-900 dark:text-purple-200">
                        Zone {zone || "N/A"}
                      </span>
                    </div>

                    {/* 4 pricing mode chips: surface / express / surface_per500 / express_per500 */}
                    <div className="grid grid-cols-2 gap-1.5 px-1 mt-3">
                      {Object.keys(MODE_CONFIG).map((modeKey) => {
                        const isAvailable = !!modes[modeKey];
                        const isActiveChip = mode === modeKey;
                        const Icon = MODE_CONFIG[modeKey].icon;

                        return (
                          <button
                            key={modeKey}
                            type="button"
                            onClick={() => isAvailable && setModeFor(courier, modeKey)}
                            disabled={!isAvailable}
                            className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold py-1.5 mb-1  rounded-lg border transition-all leading-tight
                              ${isActiveChip
                                ? 'bg-customPurple text-white border-customPurple'
                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600'}
                              ${!isAvailable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <Icon size={12} />
                            <span className="text-center">{MODE_CONFIG[modeKey].label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Selected mode ka total price */}
                    {/* <div className="text-center mt-1">
                      <p className="text-md font-bold text-slate-800 dark:text-white flex items-center justify-center">
                        <RiMoneyRupeeCircleFill className="mr-1 text-customPurple" size={20} />
                        {pricing ? pricing.total_price : "N/A"}
                      </p>
                      <p className="text-[11px] text-slate-400">
                        {MODE_CONFIG[mode]?.label || mode} shipping
                      </p>
                    </div> */}

                    {/* Full calculation breakdown of the selected mode */}
                    <div className="px-3 py-1 mt-2 space-y-2.5 text-xs border-t border-slate-100 dark:border-slate-700">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Unit Price</span>
                        <span className="font-medium text-slate-700 dark:text-white">
                          {pricing?.unit_price ? `₹ ${pricing.unit_price}` : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Base Price</span>
                        <span className="font-medium text-slate-700 dark:text-white">
                          {pricing?.base_price !== undefined ? `₹ ${pricing.base_price}` : "N/A"}
                        </span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Additional Price</span>
                        <span className="font-medium text-slate-700 dark:text-white">
                          {pricing?.additional_price !== undefined ? `₹ ${pricing.additional_price}` : "N/A"}
                        </span>
                      </div> */}
                      {/* <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Additional Wt.</span>
                        <span className="font-medium text-slate-700 dark:text-white">
                          {pricing?.additional_gm !== undefined ? `${pricing.additional_gm} gm` : "N/A"}
                        </span>
                      </div> */}
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Calculated Weight</span>
                        <span className="font-medium text-slate-700 dark:text-white">
                          {pricing?.calculated_weight !== undefined ? `${pricing.calculated_weight} gm` : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">COD Extra</span>
                        <span className="font-medium text-slate-700 dark:text-white">
                          {pricing?.cod_extra ? `₹ ${pricing.cod_extra}` : "0"}
                        </span>
                      </div>
                      <div className="flex justify-between pt-1.5 border-t border-dashed border-slate-200 dark:border-slate-600">
                        <span className="text-slate-600 dark:text-slate-300 font-semibold">Total</span>
                        <span className="font-bold text-customPurple dark:text-purple-300">
                          {pricing
                            ? `₹ ${pricing.cod_total_price !== 0 ? pricing.cod_total_price : pricing.total_price}`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-sm text-slate-400 py-10">
              No data available
            </div>
          )}
        </DialogContent>

        <div className="flex justify-center md:justify-end px-2  mt-0 bg-white dark:bg-darkmodeColor">
          <button onClick={handleNavigation}
            className="w-full md:w-auto px-2 pb-2 font-semibold font-sans text-black rounded-md flex items-center justify-center">
            <IoMdArrowRoundBack />
            Create Forward Order
          </button>
        </div>
      </Dialog>
    </>

  );
};
export default ViewRateForword;